import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { geocodeAddress } from '../../utils/geocoding';
import {
  CREW_JOB_MAP_STYLES,
  buildCrewJobMapPins,
  crewJobHasHotel,
  crewJobHasMapletCopy,
  formatCrewJobHotelLine,
  googleMapsSearchUrl,
  optionalCrewCoord,
  optionalCrewText,
  type CrewJobMapSource,
} from '../../utils/crewJobsMap';

type Props = {
  job: CrewJobMapSource;
};

const PIN_COLORS = {
  jobsite: '#8a6a3d',
  hotel: '#3d5a8a',
} as const;

function crewPinIcon(color: string): google.maps.Icon {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
    <path fill="${color}" stroke="#fffdf8" stroke-width="2"
      d="M14 2c-6.6 0-12 5.3-12 11.8 0 8.8 12 20.2 12 20.2s12-11.4 12-20.2C26 7.3 20.6 2 14 2z"/>
    <circle cx="14" cy="13.2" r="4.2" fill="#fffdf8"/>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(28, 36),
    anchor: new google.maps.Point(14, 34),
  };
}

const CrewJobMaplet: React.FC<Props> = ({ job }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded } = useJsApiLoader({
    id: 'crew-job-maplet',
    googleMapsApiKey: apiKey,
  });
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [resolved, setResolved] = useState<CrewJobMapSource>(job);

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      const next: CrewJobMapSource = { ...job };
      const siteAddress = optionalCrewText(job.location);
      if (
        siteAddress &&
        (optionalCrewCoord(job.locationLat) == null || optionalCrewCoord(job.locationLng) == null)
      ) {
        const geo = await geocodeAddress(siteAddress);
        if (geo) {
          next.locationLat = geo.lat;
          next.locationLng = geo.lng;
        }
      }
      const hotelAddress = optionalCrewText(job.hotelLocation);
      if (
        hotelAddress &&
        (optionalCrewCoord(job.hotelLat) == null || optionalCrewCoord(job.hotelLng) == null)
      ) {
        const geo = await geocodeAddress(hotelAddress);
        if (geo) {
          next.hotelLat = geo.lat;
          next.hotelLng = geo.lng;
        }
      }
      if (!cancelled) setResolved(next);
    };
    void resolve();
    return () => {
      cancelled = true;
    };
  }, [job]);

  const pins = useMemo(() => buildCrewJobMapPins(resolved), [resolved]);
  const hotelLine = formatCrewJobHotelLine(job.hotelName, job.hotelLocation);
  const siteAddress = optionalCrewText(job.location);
  const showHotel = crewJobHasHotel(job);

  const fitPins = (map: google.maps.Map, nextPins: typeof pins) => {
    google.maps.event.trigger(map, 'resize');
    if (nextPins.length === 0) return;
    if (nextPins.length === 1) {
      map.setCenter({ lat: nextPins[0].lat, lng: nextPins[0].lng });
      map.setZoom(12);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    nextPins.forEach(pin => bounds.extend({ lat: pin.lat, lng: pin.lng }));
    map.fitBounds(bounds, 40);
  };

  const syncMarkers = (map: google.maps.Map, nextPins: typeof pins) => {
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = nextPins.map(pin => new google.maps.Marker({
      map,
      position: { lat: pin.lat, lng: pin.lng },
      title: pin.label,
      optimized: false,
      icon: crewPinIcon(PIN_COLORS[pin.id]),
    }));
    fitPins(map, nextPins);
  };

  useEffect(() => {
    if (mapRef.current) syncMarkers(mapRef.current, pins);
  }, [pins]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const ro = new ResizeObserver(() => {
      if (mapRef.current) fitPins(mapRef.current, pins);
    });
    ro.observe(frame);
    return () => ro.disconnect();
  }, [pins, isLoaded]);

  if (!crewJobHasMapletCopy(job)) return null;

  return (
    <aside className="crew-board-maplet" aria-label="Jobsite and hotel map">
      <h2>Location</h2>
      {apiKey && isLoaded && pins.length > 0 && (
        <div className="crew-board-maplet-frame" ref={frameRef}>
          <GoogleMap
            mapContainerClassName="crew-board-maplet-canvas"
            mapContainerStyle={{ width: '100%', height: '188px' }}
            center={{ lat: pins[0].lat, lng: pins[0].lng }}
            zoom={12}
            onLoad={map => {
              mapRef.current = map;
              requestAnimationFrame(() => syncMarkers(map, pins));
              window.setTimeout(() => syncMarkers(map, pins), 250);
            }}
            onUnmount={() => {
              markersRef.current.forEach(marker => marker.setMap(null));
              markersRef.current = [];
              mapRef.current = null;
            }}
            options={{
              disableDefaultUI: true,
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              clickableIcons: false,
              maxZoom: 16,
              minZoom: 6,
              styles: CREW_JOB_MAP_STYLES,
            }}
          />
        </div>
      )}
      <ul className="crew-board-maplet-legend">
        {siteAddress && (
          <li>
            <span className="crew-board-maplet-dot crew-board-maplet-dot--site" aria-hidden />
            <div>
              <strong>Jobsite</strong>
              <p>{siteAddress}</p>
              <a href={googleMapsSearchUrl(siteAddress)} target="_blank" rel="noreferrer">
                Open in Maps
              </a>
            </div>
          </li>
        )}
        {showHotel && hotelLine && (
          <li>
            <span className="crew-board-maplet-dot crew-board-maplet-dot--hotel" aria-hidden />
            <div>
              <strong>Hotel</strong>
              <p>{hotelLine}</p>
              <a
                href={googleMapsSearchUrl(optionalCrewText(job.hotelLocation) || hotelLine)}
                target="_blank"
                rel="noreferrer"
              >
                Open in Maps
              </a>
            </div>
          </li>
        )}
      </ul>
    </aside>
  );
};

export default CrewJobMaplet;
